import { Logger } from '~~/helpers/logger';
import type { AxiosError } from 'axios';
import type { GrahamProductReview } from '~~/types/product/attribute/GrahamProductReview';
import { PRODUCT_REVIEW_ERROR } from '~~/types/product/attribute/ProductReview';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import { errorToString } from '~~/helpers/error/errorToString';

import { ALLOWED_METHODS } from '~~/types/expondoMiddleware';

const errorCallback = (error: AxiosError) => {

  if (error) {

    // `makeMiddlewareRequest` uses `$fetch` (ofetch). On non-2xx it throws a FetchError,
    // not an AxiosError. Support both shapes so UI can display expected messages.
    const anyError = error as unknown as {
      response?: { data?: any; _data?: any };
      data?: any;
    };

    const communicate =
      anyError?.response?.data?.communicate ??
      anyError?.response?._data?.communicate ??
      anyError?.data?.communicate;

    if (
      communicate &&
      Object.values(PRODUCT_REVIEW_ERROR).includes(communicate)
    ) {

      throw new Error(communicate);
    
    } else {

      throw new Error('Something went wrong on our side. Please try again or contact support');
    
    }
  
  }

};

export const useManageReviews = () => {

  const errorMessage = ref('');
  const isReviewAdded = ref<boolean | undefined>(undefined);
  const { makeMiddlewareRequest } = useExpondoMiddleware();

  const addReview = async (productReview: GrahamProductReview) => {

    isReviewAdded.value = false;
    try {

      await makeMiddlewareRequest({
        endpoint: EXPONDO_MIDDLEWARE_ROUTES.ADD_REVIEW,
        method: ALLOWED_METHODS.post,
        data: productReview,
        catchCallback: errorCallback,
      });
      isReviewAdded.value = true;
    
    } catch (error: unknown) {

      const errorString = errorToString(error);
      errorMessage.value = errorString;
      Logger.error('Error during adding new review: ' + errorString);
    
    }
  
  };

  return {
    addReview,
    errorMessage,
    isReviewAdded,
  };

};
